import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customInstance } from '../../services/api-client';
import type {
  APConnectorOption,
  APDataSource,
  APDataSourceConfigUpdate,
  APHealth,
  APRun,
} from './types';

const BASE = '/api/ap-cutoff-enforcer';

export const apCutoffKeys = {
  all: ['ap-cutoff-enforcer'] as const,
  run: (period: string) => [...apCutoffKeys.all, 'run', period] as const,
  config: () => [...apCutoffKeys.all, 'config'] as const,
  connectors: () => [...apCutoffKeys.all, 'available-connectors'] as const,
};

export function useAPCutoffRun(period: string) {
  return useQuery<APRun>({
    queryKey: apCutoffKeys.run(period),
    queryFn: () =>
      customInstance({
        url: `${BASE}/run`,
        method: 'GET',
        params: { period },
      }),
  });
}

export function useAPCutoffDataSourceConfig() {
  return useQuery<APDataSource>({
    queryKey: apCutoffKeys.config(),
    queryFn: () => customInstance({ url: `${BASE}/config/data-source`, method: 'GET' }),
    staleTime: 60_000,
  });
}

export function useAPCutoffAvailableConnectors() {
  return useQuery<APConnectorOption[]>({
    queryKey: apCutoffKeys.connectors(),
    queryFn: () => customInstance({ url: `${BASE}/config/available-connectors`, method: 'GET' }),
    staleTime: 60_000,
  });
}

export function useSetAPCutoffDataSource() {
  const queryClient = useQueryClient();
  return useMutation<APDataSource, Error, APDataSourceConfigUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data: payload,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: apCutoffKeys.all }),
  });
}

export function useClearAPCutoffDataSource() {
  const queryClient = useQueryClient();
  return useMutation<APDataSource, Error, void>({
    mutationFn: () =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: apCutoffKeys.all }),
  });
}

export function useTestAPCutoffConnection() {
  return useMutation<APHealth, Error, APDataSourceConfigUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/test-connection`,
        method: 'POST',
        data: payload,
      }),
  });
}
